import React, { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { MdFlashlightOff, MdFlashlightOn, MdOutlineFileUpload } from "react-icons/md";
import { cssProperties } from "../../utils/commonCssProperties";
import { parseScannedQr, SCAN_TYPE } from "./parseScannedQr";
import { DEFAULT_CROP, startAutoZoom } from "./autoZoom";

const INVALID_QR = "This QR code is not from an MHT-CET admit card. Please scan the admit card QR code.";

// Why the camera could not be opened, in words a verifier can act on.
// qr-scanner reports every failure as "Camera not found.", so the cause is
// worked out from the permission state and the device list instead.
const cameraErrorMessage = async () => {
  if (!window.isSecureContext || !navigator.mediaDevices) {
    return "The camera can only be used on a secure (https) connection.";
  }
  const permission = await navigator.permissions
    ?.query({ name: "camera" })
    .then((p) => p.state)
    .catch(() => null);
  if (permission === "denied") {
    return "Camera access was denied. Allow camera access for this site in your browser settings, then try again.";
  }
  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => null);
  if (devices && !devices.some((d) => d.kind === "videoinput")) {
    return "No camera was found on this device.";
  }
  return "The camera could not be started. Close any other app using it and try again, or allow camera access for this site.";
};

// The library's default region is the middle 2/3 of the frame scaled to 400px.
// Verifiers tend to fill the frame with the QR (as with a camera app), which
// pushes its corner markers outside that, and the sealed payload makes a dense
// QR. So read most of the frame, at up to 720px. Auto zoom without camera zoom
// passes a smaller `crop` to read just the centre at full detail.
const scanRegion = (video, crop = DEFAULT_CROP) => {
  const size = Math.round(crop * Math.min(video.videoWidth, video.videoHeight));
  const scaled = Math.min(size, 720);
  return {
    x: Math.round((video.videoWidth - size) / 2),
    y: Math.round((video.videoHeight - size) / 2),
    width: size,
    height: size,
    downScaledWidth: scaled,
    downScaledHeight: scaled,
  };
};

// Scans an admit-card QR with the device camera (or from a photo of it) and
// hands the parsed result to onScan once. Remount it to scan again.
// `parse` / `invalidMessage` default to the Data Verifier's; the public
// roll-number page passes its own parser (it needs the full signed link).
export default function AdmitCardQrScanner({ onScan, parse = parseScannedQr, invalidMessage = INVALID_QR }) {
  const viewportRef = useRef(null);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  // A camera reads the same code many times a second; only the first valid
  // read is used.
  const doneRef = useRef(false);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const [cameraState, setCameraState] = useState("starting"); // starting | live | failed
  const [cameraError, setCameraError] = useState("");
  const [scanMessage, setScanMessage] = useState("");
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [zoom, setZoom] = useState(1);
  const stopAutoZoomRef = useRef(null);

  // Returns true when the text was a usable admit-card QR.
  const handleText = (text) => {
    if (doneRef.current) return true;
    const parsed = parse(text);
    if (parsed.type === SCAN_TYPE.INVALID) {
      setScanMessage(invalidMessage);
      return false;
    }
    doneRef.current = true;
    stopAutoZoomRef.current?.();
    scannerRef.current?.stop();
    onScanRef.current(parsed);
    return true;
  };
  const handleTextRef = useRef(handleText);
  handleTextRef.current = handleText;

  useEffect(() => {
    let cancelled = false;
    // Each scanner gets its own <video>: a destroyed scanner stops whatever
    // stream is on its video ~300ms later, which would otherwise kill the
    // stream of a scanner started right after it (a quick remount, or React's
    // development double-mount).
    const frame = document.createElement("div");
    frame.style.cssText = "position:absolute;inset:0;";
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.style.cssText = "width:100%;height:100%;object-fit:cover;";
    frame.appendChild(video);
    viewportRef.current.appendChild(frame);

    // Share of the frame that is scanned; auto zoom narrows it when the camera
    // has no zoom of its own.
    let crop = DEFAULT_CROP;
    const scanner = new QrScanner(
      video,
      (result) => handleTextRef.current(result?.data),
      {
        preferredCamera: "environment",
        returnDetailedScanResult: true,
        highlightScanRegion: true,
        highlightCodeOutline: true,
        maxScansPerSecond: 10,
        calculateScanRegion: (v) => scanRegion(v, crop),
      }
    );
    scannerRef.current = scanner;

    // qr-scanner (1.4.x) works out the scan region only when the video starts;
    // refresh its cached copy and the on-screen frame after a zoom step or a
    // change of camera resolution.
    const refreshScanRegion = () => {
      if (!("_scanRegion" in scanner) || !video.videoWidth) return;
      scanner._scanRegion = scanRegion(video, crop);
      scanner._updateOverlay?.();
    };
    video.addEventListener("resize", refreshScanRegion);

    scanner
      .start()
      .then(async () => {
        if (cancelled) return;
        setCameraState("live");
        stopAutoZoomRef.current = startAutoZoom({
          scanner,
          video,
          setCrop: (value) => {
            crop = value;
            refreshScanRegion();
          },
          onZoomChange: (factor) => {
            if (!cancelled) setZoom(factor);
          },
        });
        const flash = await scanner.hasFlash().catch(() => false);
        if (!cancelled) setHasFlash(flash);
      })
      .catch(async () => {
        if (cancelled) return;
        const reason = await cameraErrorMessage();
        if (cancelled) return;
        setCameraError(reason);
        setCameraState("failed");
      });

    // Stops the camera whenever the scanner leaves the screen.
    return () => {
      cancelled = true;
      stopAutoZoomRef.current?.();
      stopAutoZoomRef.current = null;
      video.removeEventListener("resize", refreshScanRegion);
      scanner.destroy();
      frame.remove();
      scannerRef.current = null;
    };
  }, []);

  const toggleFlash = () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    scanner
      .toggleFlash()
      .then(() => setFlashOn(scanner.isFlashOn()))
      .catch(() => setHasFlash(false));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanMessage("");
    QrScanner.scanImage(file, { returnDetailedScanResult: true, alsoTryWithoutScanRegion: true })
      .then((result) => handleTextRef.current(result?.data))
      .catch(() => setScanMessage("No QR code was found in that photo. Try a clearer, closer photo of the QR code."));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          aspectRatio: "1 / 1",
          backgroundColor: "#000",
          borderRadius: "8px",
          overflow: "hidden",
          display: cameraState === "failed" ? "none" : "block",
        }}
      >
        {/* The camera <video> is added here by the effect, not by React. */}
        <Box ref={viewportRef} sx={{ position: "absolute", inset: 0 }} />
        {cameraState === "starting" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        )}
        {cameraState === "live" && (
          <Box
            sx={{
              position: "absolute",
              left: "8px",
              bottom: "8px",
              padding: "2px 10px",
              borderRadius: "40px",
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              pointerEvents: "none",
            }}
          >
            Auto zoom {zoom.toFixed(1)}x
          </Box>
        )}
      </Box>

      {/* The camera error stays up; an upload's result shows under it, since
          uploading is what a verifier without a camera relies on. */}
      {cameraState === "failed" && (
        <Typography sx={{ fontSize: 13, fontWeight: 500, textAlign: "center", color: `${cssProperties?.color?.failure}` }}>
          {cameraError}
        </Typography>
      )}
      {(scanMessage || cameraState !== "failed") && (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            textAlign: "center",
            color: scanMessage ? `${cssProperties?.color?.failure}` : `${cssProperties?.color?.textsecondary}`,
          }}
        >
          {scanMessage ||
            "Point the camera at the QR code on the admit card. If it is far away, the camera zooms in automatically."}
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
        {cameraState === "live" && hasFlash && (
          <Button
            variant="outlined"
            onClick={toggleFlash}
            startIcon={flashOn ? <MdFlashlightOff /> : <MdFlashlightOn />}
            sx={{ borderRadius: "40px", textTransform: "none" }}
          >
            {flashOn ? "Torch off" : "Torch on"}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          startIcon={<MdOutlineFileUpload />}
          sx={{ borderRadius: "40px", textTransform: "none" }}
        >
          Upload QR photo
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFile} />
      </Box>
    </Box>
  );
}
